/** Small reusable editable list (Demo Mode only) - for free-text, repeatable single-value lists
 * (SLAs, obligations, commercial assumptions) where a full multi-column table would be overkill
 * but a flat paragraph would lose the discrete, repeatable nature of each item. Each row is a
 * plain text field with a remove button; "Add" appends a new empty row. Row spacing (2 = 16px)
 * matches the standard TextField bottom margin (MuiTextField theme override) so rows align with
 * normal form field rhythm elsewhere on the page.
 *
 * When `label` is provided, the heading and "Add" trigger render together in a header row (add
 * button opposite the title, not underneath the list) - the pattern used by the Customer
 * Workspace tabs. When omitted, the add trigger stays at the bottom of the list (legacy layout,
 * still used by callers that render their own heading above this component). */

import { Box, IconButton, Stack, TextField, Typography } from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';

interface EditableStringListProps {
  items: string[];
  onChange: (items: string[]) => void;
  addLabel: string;
  placeholder?: string;
  label?: string;
}

const EditableStringList = ({ items, onChange, addLabel, placeholder, label }: EditableStringListProps) => {
  const handleItemChange = (index: number, value: string) => {
    onChange(items.map((item, i) => (i === index ? value : item)));
  };

  const handleRemove = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    onChange([...items, '']);
  };

  const addButton = (
    <IconButton size="small"
      onClick={handleAdd}
      title={addLabel}>
      <Add fontSize="small" />
    </IconButton>
  );

  return (
    <Box>
      {label && (
        <Stack direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1.5 }}>
          <Typography variant="textSM"
            component="div"
            fontWeight={600}>{label}</Typography>
          {addButton}
        </Stack>
      )}
      <Stack spacing={2}>
        {items.map((item, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Stack direction="row"
            spacing={1}
            alignItems="center"
            key={index}>
            <TextField
              value={item}
              onChange={(e) => handleItemChange(index, e.target.value)}
              placeholder={placeholder}
              size="small"
              fullWidth
              sx={{ mb: 0 }}
            />
            <IconButton size="small"
              onClick={() => handleRemove(index)}>
              <DeleteOutline fontSize="small"
                color="error" />
            </IconButton>
          </Stack>
        ))}
      </Stack>
      {!label && (
        <Box sx={{ mt: 1.5 }}>
          {addButton}
        </Box>
      )}
    </Box>
  );
};

export default EditableStringList;
